import React, { useState, useMemo, useEffect } from 'react';
import { Input } from '../components/ui/input-simple';
import { Button } from '../components/ui/button-simple';
import { Search, MapPin, Factory, Globe, Plus, AlertCircle, Loader2, Trash2, Eye, Edit3, Filter, X, Building2, Users, TrendingUp, Package, ChevronLeft, ChevronRight, Sprout, BookOpen } from 'lucide-react';
import { municipalities } from '../data/municipalities';
import { AddProcessorForm } from '../components/forms/AddProcessorForm';
import { ProcessorImportExportButtons } from '../components/buttons/ProcessorImportExportButtons';
import { processorsAPI } from '../services/api';
import { useProcessors } from '../hooks/useApi';
import { ConfirmDeleteModal, StatusUpdateModal } from '../components/modals/ConfirmDeleteModal';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.5s ease-out forwards;
  }

  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-slide-down {
    animation: slideDown 0.6s ease-out forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .animate-slide-up {
    animation: slideUp 0.4s ease-out forwards;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-admin-processors-animations]')) {
    styleSheet.setAttribute('data-admin-processors-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function AdminProcessorsDirectory() {
  // Use API hook for processors management
  const {
    processors,
    loading,
    error,
    fetchProcessors,
    addProcessor,
    updateProcessor,
    removeProcessor
  } = useProcessors();

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedBusinessType, setSelectedBusinessType] = useState('all');
  const [selectedMarketReach, setSelectedMarketReach] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editProcessor, setEditProcessor] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, processor: null });
  const [statusModal, setStatusModal] = useState({ isOpen: false, processor: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Load processors on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchProcessors(() => processorsAPI.getAll());
      } catch (error) {
      }
    };

    loadData();
  }, [fetchProcessors]);

  // Helper function to extract municipality from address
  const extractMunicipalityFromAddress = (address) => {
    if (!address || typeof address !== 'string') return null;
    
    // Split by comma and get the parts
    const parts = address.split(',').map(part => part.trim());
    
    // Municipality is typically the second-to-last part (before province)
    // Format: "Barangay, Municipality, Province"
    if (parts.length >= 2) {
      const municipality = parts[parts.length - 2];
      if (municipality && municipality.length > 2) {
        return municipality;
      }
    }
    
    return null;
  };

  // Compute unique municipalities from processors
  const computedMunicipalities = useMemo(() => {
    if (!processors || processors.length === 0) return municipalities;
    
    const extractedMunicipalities = processors.map(p => {
      let muni = p.municipality;
      if (!muni || muni.trim() === '') {
        muni = extractMunicipalityFromAddress(p.address);
      }
      return muni;
    }).filter(Boolean);
    
    const uniqueMunicipalities = [...new Set(extractedMunicipalities)].sort();
    
    // Return the computed list or fallback to static list if empty
    return uniqueMunicipalities.length > 0 ? uniqueMunicipalities : municipalities;
  }, [processors]);

  const filteredProcessors = useMemo(() => {
    if (processors.length > 0) {
    }
    const filtered = processors.filter(processor => {
      const matchesSearch = (processor.businessName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (processor.ownerName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (processor.barangay || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      // Extract municipality with fallback to address parsing
      let processorMunicipality = processor.municipality;
      if (!processorMunicipality || processorMunicipality.trim() === '') {
        processorMunicipality = extractMunicipalityFromAddress(processor.address);
      }
      
      const matchesMunicipality = selectedMunicipality === 'all' || processorMunicipality === selectedMunicipality;
      const matchesBusinessType = selectedBusinessType === 'all' || processor.businessType === selectedBusinessType;
      const matchesMarketReach = selectedMarketReach === 'all' || processor.marketReach === selectedMarketReach;
      const matchesStatus = selectedStatus === 'all' || processor.businessStatus === selectedStatus;
      
      return matchesSearch && matchesMunicipality && matchesBusinessType && matchesMarketReach && matchesStatus;
    });
    return filtered;
  }, [processors, searchTerm, selectedMunicipality, selectedBusinessType, selectedMarketReach, selectedStatus]);

  // Paginated processors
  const paginatedProcessors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProcessors.slice(startIndex, endIndex);
  }, [filteredProcessors, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredProcessors.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMunicipality, selectedBusinessType, selectedMarketReach, selectedStatus]);

  const handleAddProcessor = (newProcessor) => {
    // The AddProcessorForm already handles the API call to POST /processors/insert_business
    // and passes back the fully-constructed processor object (with the backend-assigned id).
    // We only need to add it to the local React state here.
    addProcessor(newProcessor);
  };

  const handleEditProcessor = async (updatedProcessor) => {
    // The AddProcessorForm already handles the API call to PUT /processors/businesses/:id
    // We need to refresh the full list from the database to reflect the update
    try {
      await fetchProcessors(() => processorsAPI.getAll());
      setEditProcessor(null);
    } catch (error) {
    }
  };

  const handleBulkImport = async () => {
    // Refresh the processors list after bulk import
    await fetchProcessors(() => processorsAPI.getAll());
  };

  // Admin delete functionality
  const handleDeleteClick = (processor) => {
    setDeleteModal({ isOpen: true, processor });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.processor) return;

    setIsDeleting(true);
    try {
      // Try API delete first
      try {
        await processorsAPI.delete(deleteModal.processor.id);
      } catch (apiError) {
      }
      
      // Remove from local state using hook
      removeProcessor(deleteModal.processor.id);
      
      // Close modal and selected processor if it was deleted
      if (selectedProcessor && selectedProcessor.id === deleteModal.processor.id) {
        setSelectedProcessor(null);
      }
      
      setDeleteModal({ isOpen: false, processor: null });
    } catch (error) {
      alert('Failed to delete processor. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, processor: null });
  };

  // Status update functionality
  const handleStatusClick = (processor) => {
    setStatusModal({ isOpen: true, processor });
  };

  const handleStatusConfirm = async (newStatus) => {
    if (!statusModal.processor) return;

    setIsUpdatingStatus(true);
    try {
      // Try API update first
      try {
        await processorsAPI.updateStatus(statusModal.processor.id, newStatus);
      } catch (apiError) {
      }
      
      // Update local state
      const updatedProcessor = { ...statusModal.processor, statusOfBusiness: newStatus };
      updateProcessor(statusModal.processor.id, updatedProcessor);
      
      // Update selected processor if it's the same one
      if (selectedProcessor && selectedProcessor.id === statusModal.processor.id) {
        setSelectedProcessor(updatedProcessor);
      }
      
      setStatusModal({ isOpen: false, processor: null });
    } catch (error) {
      alert('Failed to update processor status. Please try again.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setStatusModal({ isOpen: false, processor: null });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-red-100 text-red-800';
      case 'Seasonal': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMarketReachColor = (reach) => {
    switch (reach) {
      case 'International': return 'bg-purple-100 text-purple-800';
      case 'National': return 'bg-blue-100 text-blue-800';
      case 'Regional': return 'bg-green-100 text-green-800';
      case 'Local': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Header with Gradient */}
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down relative z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Factory className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Cacao Processors Directory</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Comprehensive directory of cacao processing businesses across Bohol, including 
                  cooperatives, private enterprises, and partnerships in the cacao value chain.
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <div className="relative z-50 bg-white/95 rounded-lg p-1">
                <ProcessorImportExportButtons 
                  processors={processors}
                  onImport={handleBulkImport}
                  userRole="admin"
                />
                </div>
                <Button 
                  onClick={() => setShowAddForm(true)}
                  className="bg-green-600 text-white hover:bg-green-700 font-semibold flex items-center gap-2 px-6 shadow-lg whitespace-nowrap"
                >
                  <Plus className="h-5 w-5" />
                  Add Processor
                </Button>
              </div>
          </div>
        </div>

        {/* Modern Filters Section */}
        <div className="bg-white p-6 rounded-2xl shadow-lg mb-8 border border-gray-200 relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                placeholder="Search business or owner..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 rounded-xl border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                aria-label="Search processors"
              />
            </div>
            
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedMunicipality} 
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
                aria-label="Filter by municipality"
              >
                <option value="all">All Municipalities</option>
                {computedMunicipalities.map(municipality => (
                  <option key={municipality} value={municipality}>
                    {municipality}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedBusinessType} 
                onChange={(e) => setSelectedBusinessType(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
                aria-label="Filter by business type"
              >
                <option value="all">All Types</option>
                <option value="Cooperative">Cooperative</option>
                <option value="Private">Private</option>
                <option value="Partnership">Partnership</option>
              </select>
            </div>
            
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedMarketReach} 
                onChange={(e) => setSelectedMarketReach(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
                aria-label="Filter by market reach"
              >
                <option value="all">All Markets</option>
                <option value="Local">Local</option>
                <option value="Regional">Regional</option>
                <option value="National">National</option>
                <option value="International">International</option>
              </select>
            </div>
            
            <div className="relative">
              <TrendingUp className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              <select 
                value={selectedStatus} 
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="flex h-11 w-full rounded-xl border border-gray-300 bg-background px-3 pl-10 py-2 text-sm focus:border-amber-500 focus:ring-amber-500"
                aria-label="Filter by status"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Seasonal">Seasonal</option>
              </select>
            </div>
          </div>
          
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
              <p className="text-sm text-gray-600 font-medium">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredProcessors.length)} of {filteredProcessors.length} processors
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setSelectedMunicipality('all');
                setSelectedBusinessType('all');
                setSelectedMarketReach('all');
                setSelectedStatus('all');
              }}
              className="flex items-center gap-2 rounded-xl hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600 mr-2" />
            <span className="text-gray-600">Loading processors...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-800 font-medium">Error loading processors</span>
            </div>
            <p className="text-red-600 mt-2">{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 hover:bg-red-700"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Processors Grid */}
        {!loading && !error && filteredProcessors.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedProcessors.map((processor) => (
              <div key={processor.id} className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up" style={{ height: '450px' }}>
                {/* Card Header with Gradient */}
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-3 border-b border-amber-200 flex-shrink-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg text-amber-900 font-bold mb-1 group-hover:text-amber-700 transition-colors line-clamp-1">
                        {processor.businessName}
                      </h3>
                      <div className="flex items-center mt-1 text-gray-700">
                        <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-amber-600" />
                        <span className="text-sm">{processor.address}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(processor.businessStatus)}`}>
                      {processor.businessStatus}
                    </span>
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="p-3 flex-1 flex flex-col overflow-y-auto">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-amber-600" />
                        <span className="text-sm text-gray-600 font-medium">Owner</span>
                      </div>
                      <span className="font-semibold text-gray-900 text-sm">{processor.ownerName}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-gray-600 font-medium">Type</span>
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">{processor.businessType}</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-600 font-medium">Years Operating</span>
                      </div>
                      <span className="font-semibold text-gray-900">{processor.yearsInOperation} years</span>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-purple-600" />
                        <span className="text-sm text-gray-600 font-medium">Market Reach</span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getMarketReachColor(processor.marketReach)}`}>
                        {processor.marketReach}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-orange-600" />
                        <span className="text-sm text-gray-600 font-medium">Capacity</span>
                      </div>
                      <span className="font-semibold text-gray-900">{processor.processingCapacity} kg/mo</span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-1.5 mt-2 flex-shrink-0">
                    <Button 
                      className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold flex items-center justify-center gap-2 rounded-xl shadow-md hover:shadow-lg transition-all py-2 h-9"
                      onClick={() => setSelectedProcessor(processor)}
                    >
                      <Eye className="h-4 w-4" />
                      View Full Profile
                    </Button>
                    
                    <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          className="flex-1 text-green-600 border-2 border-green-300 hover:bg-green-50 hover:border-green-500 rounded-xl transition-all flex items-center justify-center gap-2 py-2 h-9"
                          onClick={() => setEditProcessor(processor)}
                          title="Edit Processor"
                        >
                          <Edit3 className="h-4 w-4" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 text-blue-600 border-2 border-blue-300 hover:bg-blue-50 hover:border-blue-500 rounded-xl transition-all flex items-center justify-center gap-2 py-2 h-9"
                          onClick={() => handleStatusClick(processor)}
                          title="Update Business Status"
                        >
                          <Edit3 className="h-4 w-4" />
                          Status
                        </Button>
                        <Button 
                          variant="outline"
                          className="flex-1 text-red-600 border-2 border-red-300 hover:bg-red-50 hover:border-red-500 rounded-xl transition-all flex items-center justify-center gap-2 py-2 h-9"
                          onClick={() => handleDeleteClick(processor)}
                          title="Delete Processor"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
                Previous
              </Button>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Page</span>
                <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold rounded-xl shadow-md">
                  {currentPage}
                </span>
                <span className="text-sm text-gray-600">of {totalPages}</span>
              </div>
              
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          )}
        </>
        )}

        {/* Empty State */}
        {!loading && !error && filteredProcessors.length === 0 && (
          <div className="text-center py-12">
            <Factory className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-gray-600 mb-2">No processors found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more results.</p>
          </div>
        )}

        {/* Processor Detail Modal */}
        {selectedProcessor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                        <Building2 className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Processor Profile</h2>
                        <p className="text-amber-50 text-sm">Comprehensive business information</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          setSelectedProcessor(null);
                          setEditProcessor(selectedProcessor);
                        }}
                        className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                        aria-label="Edit processor"
                        title="Edit Processor"
                      >
                        <Edit3 className="h-6 w-6" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={() => setSelectedProcessor(null)}
                        className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                        aria-label="Close profile"
                      >
                        <X className="h-7 w-7" />
                      </Button>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6 p-6 max-h-[70vh] overflow-y-auto">
                  {/* Basic Business Information */}
                  <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl border border-amber-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">Business Information</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Name of Business</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.nameOfBusiness || selectedProcessor.businessName || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Address</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.address || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Contact Number(s)</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.contactNumbers?.join(', ') || selectedProcessor.contact || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Length of Business Operation</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.lengthOfBusinessOperationInYears || selectedProcessor.yearsInOperation || 'Not specified'} years</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Status of Business</span>
                        <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedProcessor.statusOfBusiness || selectedProcessor.businessStatus)}`}>
                          {selectedProcessor.statusOfBusiness || selectedProcessor.businessStatus || 'Not specified'}
                        </span>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Type of Business</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.typeOfBusiness || selectedProcessor.businessType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Name of the Owner</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.nameOfTheOwner || selectedProcessor.ownerName || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Owner Address</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.addressOwner || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Initial Investment</span>
                        <p className="font-semibold text-green-600">₱{selectedProcessor.initialInvestment?.toLocaleString() || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Average Annual Gross Income</span>
                        <p className="font-semibold text-green-600">₱{selectedProcessor.averageAnnualGrossIncome?.toLocaleString() || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Number of Employees</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.numberOfEmployees || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Employee Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.employeeType || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Processing Operations */}
                  <div className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-2xl border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Package className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-bold text-blue-900">Processing Operations</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Quantity per Processing Cycle</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.quantityOfCacaoBeansPerProcessingCycle || selectedProcessor.processingCapacity || 'Not specified'} kg</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Frequency of Processing</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.frequencyOfProcessing || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Preferred Variety of Cacao</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.preferredVarietyOfCacao || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Variety Processed</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.varietyOfCacaoProcessed || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Source of Cacao</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.sourceOfCacao || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Producers to Processors</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.producersToProcessors || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Local Traders to Processors</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.localTradersToProcessors || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Frequency of Delivery</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.frequencyOfDelivery || 'Not specified'} per month</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Quantity Delivered</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.quantityOfCacaoDelivered || 'Not specified'} kg</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Form/Type of Cacao Bought</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.formTypeOfCacaoBought || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Form Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.formType || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Processing Methods */}
                  <div className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-2xl border border-purple-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Sprout className="h-5 w-5 text-purple-600" />
                      <h3 className="text-lg font-bold text-purple-900">Processing Methods</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Pod Breaking</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.podBreaking || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Bean Extraction</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.beanExtraction || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Fermentation</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.fermentation || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Containment Materials</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.containmentMaterials || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Number of Days</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.numberOfDays || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Temperature Monitoring</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.temperatureMonitoring || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Preferred Fermentation Temperature</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.preferredFermentationTemperature || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Drying Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.dryingReDrying || selectedProcessor.dryingType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Preferred Moisture Content</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.preferredMoistureContent || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Method of MC Determination</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.methodOfMcDetermination || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Bean Sorting</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.beanSorting || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Usual Bean Size</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.usualBeanSize || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Bean Grading</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.beanGrading || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Level of Fermentation</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.levelOfFermentation || 'Not specified'}%</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Raw Bean Aroma</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.rawBeanAroma || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Materials for Dried Bean Storage</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.materialsUsedForDriedBeanStorage || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Estimated Cost of Materials</span>
                        <p className="font-semibold text-green-600">₱{selectedProcessor.estimatedCostOfMaterials?.toLocaleString() || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Roasting</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.roasting || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Roasting Method</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.roastingMethod || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Average Time Spent</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.averageTimeSpent || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Roasting Temperature Monitoring</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.roastingTemperatureMonitoring || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Cracking Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.crackingType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Winnowing Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.winnowingType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Grinding Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.grinding || selectedProcessor.grindingType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Conching</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.conching || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Preferred Microns Size</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.preferredMicronsSize || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Tempering Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.temperingType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Moulding Type</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.mouldingType || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Storage of Processed Product</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.storageOfProcessedProduct || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Preferred Storage Temperature</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.preferredStorageTemperature || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Preferred Relative Humidity</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.preferredRelativeHumidity || 'Not specified'}%</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Packaging Material</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.packagingMaterial || 'Not specified'}</p>
                      </div>
                  </div>
                </div>

                  {/* Marketing & Sales */}
                  <div className="bg-gradient-to-br from-green-50 to-white p-6 rounded-2xl border border-green-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      <h3 className="text-lg font-bold text-green-900">Marketing</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl md:col-span-2">
                        <span className="text-sm text-gray-600">Type of Product Sold</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedProcessor.typeOfProductSold ? 
                            (Array.isArray(selectedProcessor.typeOfProductSold) ? 
                              selectedProcessor.typeOfProductSold.map((product, index) => (
                                <span key={index} className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                                  {product}
                                </span>
                              )) :
                              <p className="font-semibold text-gray-900">{selectedProcessor.typeOfProductSold}</p>
                            ) : 
                            <span className="font-semibold text-gray-900">Not specified</span>
                          }
                        </div>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Type of Marketing</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.typeOfMarketing || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Market</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.market || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Marketplace</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.marketplace || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Local Market</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.localMarket ? 'Yes' : 'No'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">International</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.international ? 'Yes' : 'No'}</p>
                      </div>
                  </div>
                </div>

                  {/* Business Insights */}
                  <div className="bg-gradient-to-br from-indigo-50 to-white p-6 rounded-2xl border border-indigo-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="h-5 w-5 text-indigo-600" />
                      <h3 className="text-lg font-bold text-indigo-900">Business Insights & Plans</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="bg-white p-4 rounded-xl">
                        <span className="text-sm text-gray-600 font-medium block mb-2">What interest you to go on cacao processing?</span>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProcessor.whatInterestYouToGoOnCacaoProcessing || 'Not specified'}</div>
                      </div>
                      <div className="bg-white p-4 rounded-xl">
                        <span className="text-sm text-gray-600 font-medium block mb-2">Challenges encountered in processing</span>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProcessor.challengesEncounteredInProcessing || (selectedProcessor.challenges?.join(', ')) || 'Not specified'}</div>
                      </div>
                      <div className="bg-white p-4 rounded-xl">
                        <span className="text-sm text-gray-600 font-medium block mb-2">Supports needed to improve business</span>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProcessor.supportsNeededToImprove || 'Not specified'}</div>
                      </div>
                      <div className="bg-white p-4 rounded-xl">
                        <span className="text-sm text-gray-600 font-medium block mb-2">Future plans</span>
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">{selectedProcessor.futurePlans || 'Not specified'}</div>
                      </div>
                    </div>
                  </div>

                  {/* GPS Location */}
                  <div className="bg-gradient-to-br from-cyan-50 to-white p-6 rounded-2xl border border-cyan-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin className="h-5 w-5 text-cyan-600" />
                      <h3 className="text-lg font-bold text-cyan-900">GPS Location</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Latitude</span>
                        <p className="font-semibold text-gray-900">
                          {typeof selectedProcessor.latitude === 'number' ? 
                            selectedProcessor.latitude.toFixed(6) : 
                            selectedProcessor.latitude || 'Not specified'}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Longitude</span>
                        <p className="font-semibold text-gray-900">
                          {typeof selectedProcessor.longitude === 'number' ? 
                            selectedProcessor.longitude.toFixed(6) : 
                            selectedProcessor.longitude || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Processor Form */}
        {showAddForm && (
          <AddProcessorForm 
            onClose={() => setShowAddForm(false)}
            onAddProcessor={handleAddProcessor}
          />
        )}

        {/* Edit Processor Form */}
        {editProcessor && (
          <AddProcessorForm 
            onClose={() => setEditProcessor(null)}
            onAddProcessor={handleEditProcessor}
            editData={editProcessor}
          />
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={deleteModal.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Processor"
          message="Are you sure you want to delete this processor from the directory?"
          itemName={deleteModal.processor ? deleteModal.processor.nameOfBusiness || deleteModal.processor.businessName : ''}
          isLoading={isDeleting}
        />

        {/* Status Update Modal */}
        <StatusUpdateModal
          isOpen={statusModal.isOpen}
          onClose={handleStatusCancel}
          onConfirm={handleStatusConfirm}
          currentStatus={statusModal.processor?.statusOfBusiness}
          itemName={statusModal.processor ? statusModal.processor.nameOfBusiness || statusModal.processor.businessName : ''}
          isLoading={isUpdatingStatus}
        />
      </div>
    </div>
  );
}